import { Component, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import {
  ProtocolTypeDetailDto,
  ProtocolTypeMergeDto,
  ProtocolTypeService,
} from '@artemis-software/wr-api';
import { DeleteDialogComponent } from '@components/dialog/delete-dialog/delete-dialog.component';
import { createForm } from '@sonofabit/ng-core';
import { Validators } from '@angular/forms';
import { PendingChanges } from '@guards/pending-changes.guard';

@Component({
  standalone: false,
  selector: 'wr-protocol-type-form',
  templateUrl: './protocol-type-form.component.html',
  styleUrls: ['./protocol-type-form.component.scss'],
})
export class ProtocolTypeFormComponent implements PendingChanges {

  readonly saving$ = new BehaviorSubject(false);
  readonly isEditing = signal(false);
  readonly canEdit = signal(false);
  readonly isNew = signal(false);

  protocolTypeId: string | null = null;

  form = this.buildForm(null);

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly protocolTypeService: ProtocolTypeService,
    private readonly dialogService: NbDialogService,
    private readonly toastrService: NbToastrService,
  ) {
    this.route.paramMap.subscribe(params => {
      const id = this.protocolTypeId = params.get('id');
      if (id === 'new') {
        this.isNew.set(true);
        this.isEditing.set(true);
        this.canEdit.set(true);
        this.form = this.buildForm(null);
      } else if (id) {
        this.protocolTypeService.findById(id).subscribe(dto => {
          this.canEdit.set(dto.permissions.canEdit);
          this.form = this.buildForm(dto);
          if (!this.isEditing()) {
            this.form.disable();
          }
        });
      }
    });
  }

  buildForm(dto: ProtocolTypeDetailDto | null) {
    return createForm(({ field }) => ({
      id: field({ value: dto?.id }),
      shortcut: field({ value: dto?.shortcut ?? '', validators: [Validators.required] }),
      name: field({ value: dto?.name ?? '', validators: [Validators.required] }),
      color: field({ value: dto?.color ?? '#3366FF' }),
      description: field({ value: dto?.description ?? '' }),
    }));
  }

  onEdit(): void {
    this.isEditing.set(true);
    this.form.enable();
  }

  async cancel(): Promise<void> {
    if (this.isNew()) {
      this.router.navigate(['protocolTypes']);
      return;
    }
    this.isEditing.set(false);
    const id = this.protocolTypeId;
    if (id) {
      const dto = await firstValueFrom(this.protocolTypeService.findById(id));
      this.form = this.buildForm(dto);
      this.form.disable();
    }
  }

  async submit(): Promise<boolean> {
    this.form.markAllAsTouched();
    if (!this.form.valid) {
      this.toastrService.danger('Bitte alle Pflichtfelder ausfüllen.', 'Protokolltyp');
      return false;
    }
    try {
      this.saving$.next(true);
      const mergeDto: ProtocolTypeMergeDto = { ...this.form.value };
      const saved = await firstValueFrom(this.protocolTypeService.merge(mergeDto));
      this.isEditing.set(false);
      this.toastrService.success('Protokolltyp gespeichert', 'Erfolg');
      if (this.isNew()) {
        this.router.navigate(['protocolTypes', saved.id], { replaceUrl: true });
      } else {
        const dto = await firstValueFrom(this.protocolTypeService.findById(saved.id));
        this.form = this.buildForm(dto);
        this.form.disable();
      }
      return true;
    } catch {
      this.toastrService.danger('Protokolltyp konnte nicht gespeichert werden.', 'Fehler');
      return false;
    } finally {
      this.saving$.next(false);
    }
  }

  onDelete(): void {
    this.dialogService.open(DeleteDialogComponent, {
      context: {
        title: 'Protokolltyp löschen',
        message: 'Möchten Sie diesen Protokolltyp wirklich löschen?',
      },
    }).onClose.subscribe(async (confirmed) => {
      if (confirmed && this.protocolTypeId) {
        await firstValueFrom(this.protocolTypeService.deleteById(this.protocolTypeId));
        this.toastrService.success('Protokolltyp gelöscht', 'Erfolg');
        this.router.navigate(['protocolTypes']);
      }
    });
  }

  hasPendingChanges(): boolean {
    return this.isEditing();
  }
}
